import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  User, Calendar, Clock, ArrowLeft, Share2, 
  Facebook, Twitter, Linkedin, Mail, Loader2 
} from 'lucide-react';
import {
  upsertMetaByName,
  upsertMetaByProperty,
  upsertCanonical,
  upsertJsonLd,
  removeJsonLd,
  stripHtml
} from '../lib/seoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://vidya365.in';

// Scan post HTML for H2/H3 headings that look like questions and pair each
// with the prose immediately following it — gives us a free FAQ schema.
const extractFaqsFromContent = (html = '') => {
  if (!html || typeof document === 'undefined') return [];
  try {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const headings = wrapper.querySelectorAll('h2, h3');
    const faqs = [];
    headings.forEach((h) => {
      const question = (h.textContent || '').trim();
      if (!question || !question.endsWith('?')) return;
      // Collect text of the next 1-3 sibling elements as the answer
      let answerParts = [];
      let sib = h.nextElementSibling;
      let hops = 0;
      while (sib && hops < 3 && !/^H[1-6]$/i.test(sib.tagName)) {
        const piece = (sib.textContent || '').trim();
        if (piece) answerParts.push(piece);
        sib = sib.nextElementSibling;
        hops += 1;
      }
      const answer = answerParts.join(' ').replace(/\s+/g, ' ').trim();
      if (answer.length > 20) {
        faqs.push({ question, answer: answer.slice(0, 500) });
      }
    });
    return faqs.slice(0, 8);
  } catch (_) {
    return [];
  }
};

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchBlogPost();
    fetchRelatedPosts();
  }, [id]);

  // Inject per-post SEO (title, description, canonical, OG tags, Article + Breadcrumb JSON-LD)
  useEffect(() => {
    if (!post) return;
    const plainDescription = post.excerpt?.trim() || stripHtml(post.content, 160);
    const title = `${post.title} | Vidya365 Latest Update`;
    const canonical = `${SITE_URL}/latest-updates/${post.id}`;
    // Use the dynamic OG image endpoint if no explicit featured_image is set.
    // The image renders branded, category-coloured cards with the post title.
    const apiBase = process.env.REACT_APP_BACKEND_URL || SITE_URL;
    const ogImageUrl = `${apiBase}/api/blog/og/${post.id}.png`;
    const image = post.featured_image || ogImageUrl;
    const isoCreated = post.created_at ? new Date(post.created_at).toISOString() : new Date().toISOString();
    const isoUpdated = post.updated_at ? new Date(post.updated_at).toISOString() : isoCreated;
    const keywords = [
      post.category,
      post.title,
      'Vidya365',
      'SSC preparation',
      'Banking exam',
      'RBI Grade B',
      'coaching Karol Bagh'
    ].filter(Boolean).join(', ');

    document.title = title;
    upsertMetaByName('description', plainDescription);
    upsertMetaByName('keywords', keywords);
    upsertMetaByName('author', post.author_name || 'Vidya365');
    upsertCanonical(canonical);
    upsertMetaByProperty('og:title', title);
    upsertMetaByProperty('og:description', plainDescription);
    upsertMetaByProperty('og:url', canonical);
    upsertMetaByProperty('og:type', 'article');
    upsertMetaByProperty('og:image', image);
    upsertMetaByProperty('article:published_time', isoCreated);
    upsertMetaByProperty('article:modified_time', isoUpdated);
    if (post.category) upsertMetaByProperty('article:section', post.category);
    upsertMetaByName('twitter:card', 'summary_large_image');
    upsertMetaByName('twitter:title', title);
    upsertMetaByName('twitter:description', plainDescription);
    upsertMetaByName('twitter:image', image);

    // Article schema — core rich-result unlock for blog posts
    upsertJsonLd('blog-article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: plainDescription,
      image: [image],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical
      },
      datePublished: isoCreated,
      dateModified: isoUpdated,
      author: {
        '@type': 'Person',
        name: post.author_name || 'Vidya365 Team'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Vidya365',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo.png`
        }
      },
      articleSection: post.category || 'Updates',
      keywords
    });

    // Breadcrumb — "Home › Latest Update › {Post Title}"
    upsertJsonLd('blog-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Latest Update', item: `${SITE_URL}/latest-updates` },
        { '@type': 'ListItem', position: 3, name: post.title, item: canonical }
      ]
    });

    // Auto-generated FAQ schema from H2/H3 headings in the content that end
    // with a question mark. Falls back to standard coaching FAQs if the post
    // doesn't contain any Q-style sub-headings.
    const heuristicFaqs = extractFaqsFromContent(post.content);
    const fallbackFaqs = [
      {
        question: `Is this ${post.category || 'update'} relevant for SSC / Banking / RBI Grade B aspirants?`,
        answer: `Yes. This ${post.category || 'update'} is curated by Vidya365 for aspirants preparing for SSC, IBPS, SBI, RBI Grade B and other competitive exams.`
      },
      {
        question: 'Where can I get coaching for this exam?',
        answer: 'Vidya365 offers the best SSC, Banking and RBI Grade B coaching in Karol Bagh, Delhi with both online and offline classes, comprehensive study material and mock tests.'
      }
    ];
    const faqs = heuristicFaqs.length ? heuristicFaqs : fallbackFaqs;

    upsertJsonLd('blog-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer }
      }))
    });

    // Cleanup when the component unmounts (back to Latest Update list, etc.)
    return () => {
      removeJsonLd('blog-article');
      removeJsonLd('blog-breadcrumb');
      removeJsonLd('blog-faq');
    };
  }, [post]);

  const fetchBlogPost = async () => {
    try {
      console.log('🔍 Fetching blog post:', id);
      const response = await fetch(`${API_URL}/api/blog/${id}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Blog post received:', data.title);
        setPost(data);
      } else {
        console.error('❌ Blog post not found');
        setPost(null);
      }
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching blog post:', error);
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog`);
      const data = await response.json();
      // Get 3 random posts excluding current
      const filtered = data.filter(p => p.id !== id);
      const shuffled = filtered.sort(() => 0.5 - Math.random());
      setRelatedPosts(shuffled.slice(0, 3));
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="animate-spin text-red-600" size={48} />
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex flex-col items-center justify-center py-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-6">The blog post you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/latest-updates')} className="bg-red-600 hover:bg-red-700">
            <ArrowLeft className="mr-2" size={16} />
            Back to Latest Update
          </Button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Back Button */}
          <Button 
            onClick={() => navigate('/latest-updates')} 
            variant="outline" 
            className="mb-6"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Latest Update
          </Button>

          {/* Blog Post */}
          <Card>
            <CardContent className="p-8">
              {/* Category Badge */}
              <Badge className="bg-red-100 text-red-800 mb-4">
                {post.category}
              </Badge>

              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {post.title}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6 pb-6 border-b">
                <span className="flex items-center">
                  <User size={16} className="mr-2 text-red-600" />
                  {post.author_name}
                </span>
                <span className="flex items-center">
                  <Calendar size={16} className="mr-2 text-red-600" />
                  {new Date(post.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
                {post.read_time && (
                  <span className="flex items-center">
                    <Clock size={16} className="mr-2 text-red-600" />
                    {post.read_time}
                  </span>
                )}
              </div>

              {/* Featured Image (LCP element on blog detail — load eagerly + high priority) */}
              {post.featured_image && (
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  width="1200"
                  height="384"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  className="w-full h-96 object-cover rounded-lg mb-8"
                />
              )}

              {/* Excerpt */}
              {post.excerpt && (
                <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-8">
                  <p className="text-lg text-gray-700 italic">
                    {post.excerpt}
                  </p>
                </div>
              )}

              {/* Content */}
              <div className="prose prose-lg max-w-none blog-content">
                <div 
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </div>

              {/* PDF Download if available */}
              {post.pdf_url && (
                <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Download PDF</h3>
                      <p className="text-sm text-gray-600">Get this article in PDF format</p>
                    </div>
                    <a
                      href={`${API_URL}/api/blog/pdf/${post.id}`}
                      download
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download PDF
                    </a>
                  </div>
                </div>
              )}

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Share2 className="mr-2 text-red-600" size={20} />
                    Share this article
                  </h3>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => sharePost('facebook')}
                      variant="outline"
                      size="sm"
                      className="hover:bg-blue-50"
                    >
                      <Facebook size={16} className="text-blue-600" />
                    </Button>
                    <Button
                      onClick={() => sharePost('twitter')}
                      variant="outline"
                      size="sm"
                      className="hover:bg-blue-50"
                    >
                      <Twitter size={16} className="text-blue-400" />
                    </Button>
                    <Button
                      onClick={() => sharePost('linkedin')}
                      variant="outline"
                      size="sm"
                      className="hover:bg-blue-50"
                    >
                      <Linkedin size={16} className="text-blue-700" />
                    </Button>
                    <Button
                      onClick={() => sharePost('email')}
                      variant="outline"
                      size="sm"
                      className="hover:bg-gray-100"
                    >
                      <Mail size={16} className="text-gray-600" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Card 
                    key={relatedPost.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/latest-updates/${relatedPost.id}`)}
                  >
                    {relatedPost.featured_image && (
                      <img 
                        src={relatedPost.featured_image} 
                        alt={relatedPost.title}
                        width="400"
                        height="160"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-40 object-cover rounded-t-lg"
                      />
                    )}
                    <CardContent className="p-4">
                      <Badge className="bg-red-100 text-red-800 mb-2 text-xs">
                        {relatedPost.category}
                      </Badge>
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-red-600">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogDetail;
